import { v2Client } from 'src/utils/apiClient'
import { createService } from '../utils/createService'
import type {
  FeatureToggle,
  CreateFeatureDto,
  UpdateFeatureDto,
  FeatureCheckResult,
  FeatureContext,
  BulkEnableDisableDto,
  BulkOperationResult,
} from 'src/interfaces/feature-toggle'
import type { PaginatedAuditResponse } from 'src/interfaces/audit-log'
import type {
  ConfigurationLevel,
  ConfigurationResponse,
  ConfigurationTargetLevel,
  ConfigurationTargetQuery,
  ResolveConfigurationQuery,
  UpdateConfigurationDto,
} from 'src/interfaces/appConfiguration'

export const configNodeService = createService(v2Client, '/config-node')

export const configurationApi = configNodeService.endpoint<
  ConfigurationResponse,
  UpdateConfigurationDto,
  UpdateConfigurationDto
>()('configuration', (_base, client, url) => ({
  resolve: (params?: ResolveConfigurationQuery) =>
    client.get<Record<string, unknown>>(`${url}/resolve`, params),

  resolvePublicValue: (key: string, params?: ResolveConfigurationQuery) =>
    client.get<unknown>(`${url}/public/resolve/${encodeURIComponent(key)}`, params),

  getLevel: (level: ConfigurationLevel) =>
    client.get<ConfigurationResponse | ConfigurationResponse[]>(
      `${url}/${encodeURIComponent(level)}`,
    ),

  getTarget: (
    level: ConfigurationTargetLevel,
    targetId: string,
    params?: ConfigurationTargetQuery,
  ) =>
    client.get<ConfigurationResponse>(
      `${url}/${encodeURIComponent(level)}/${encodeURIComponent(targetId)}`,
      params,
    ),

  postGlobal: (dto: UpdateConfigurationDto) =>
    client.post<ConfigurationResponse>(`${url}/global`, dto),

  postTarget: (
    level: ConfigurationTargetLevel,
    targetId: string,
    dto: UpdateConfigurationDto,
    params?: ConfigurationTargetQuery,
  ) =>
    client.post<ConfigurationResponse>(
      `${url}/${encodeURIComponent(level)}/${encodeURIComponent(targetId)}`,
      dto,
      params,
    ),

  putGlobal: (dto: UpdateConfigurationDto) =>
    client.put<ConfigurationResponse>(`${url}/global`, dto),

  putTarget: (
    level: ConfigurationTargetLevel,
    targetId: string,
    dto: UpdateConfigurationDto,
    params?: ConfigurationTargetQuery,
  ) =>
    client.put<ConfigurationResponse>(
      `${url}/${encodeURIComponent(level)}/${encodeURIComponent(targetId)}`,
      dto,
      params,
    ),

  patchGlobal: (dto: UpdateConfigurationDto) =>
    client.patch<ConfigurationResponse>(`${url}/global`, dto),

  patchTarget: (
    level: ConfigurationTargetLevel,
    targetId: string,
    dto: UpdateConfigurationDto,
    params?: ConfigurationTargetQuery,
  ) =>
    client.patch<ConfigurationResponse>(
      `${url}/${encodeURIComponent(level)}/${encodeURIComponent(targetId)}`,
      dto,
      params,
    ),

  deleteGlobal: () => client.del<void>(`${url}/global`),

  deleteTarget: (
    level: ConfigurationTargetLevel,
    targetId: string,
    params?: ConfigurationTargetQuery,
  ) =>
    client.del<void>(
      `${url}/${encodeURIComponent(level)}/${encodeURIComponent(targetId)}`,
      { params },
    ),
}))

export const featureApi = configNodeService.endpoint<
  FeatureToggle,
  CreateFeatureDto,
  UpdateFeatureDto
>()('features', (base, client, url) => ({
  getAll: base.getAll,
  create: base.create,
  patch: base.patch,
  remove: base.remove,
  checkFeature: (featureKey: string, context?: FeatureContext) =>
    client.get<FeatureCheckResult>(
      `${url}/${encodeURIComponent(featureKey)}/check`,
      context,
    ),
  getOverrides: (featureKey: string) =>
    client.get<FeatureToggle[]>(
      `${url}/${encodeURIComponent(featureKey)}/overrides`,
    ),
  bulkCreateOverrides: (featureKey: string, dto: BulkEnableDisableDto) =>
    client.post<BulkOperationResult>(
      `${url}/${encodeURIComponent(featureKey)}/bulk`,
      dto,
    ),
}))

export const auditApi = configNodeService.endpoint<PaginatedAuditResponse>()(
  'audit',
  (base) => ({
    get: base.get,
  }),
)
